import _ from "lodash";
import useFetchQuery from "Hooks/useFetchQuery";
import {
  getPlannerElementNodeSetIdsOfCurriculumVariables,
  getPlannerElementNodeSetData,
  getPlannerElementNodeSetsFromIdsVariables,
} from "./AssessmentLearningGoalsUtils";
import {
  getPlannerElementNodeSetIdsOfCurriculumQuery,
  getPlannerElementNodeSetsFromIdsQuery,
} from "./AssessmentLearningGoalsQueries";

/**
 * Hook implementing the two-step approach for fetching assessment learning goals data.
 * Step 1: fetch all planner element node set IDs from the curriculum.
 * Step 2: fetch the full node data for each dependency group in parallel.
 *
 * @param {{ curriculumId: string, curriculumNodeInterfaceType: string, plannerElementTypes: Array,
 *           learningGoalsQueryVariables: object, academicYearId: string }} params
 * @returns {{ learningGoalsData: Array, isLoading: boolean }}
 */
export const useAssessmentLearningGoalsData = ({
  curriculumId,
  curriculumNodeInterfaceType,
  plannerElementTypes,
  learningGoalsQueryVariables,
  academicYearId,
}) => {
  // Step 1: fetch planner element node set IDs from curriculum
  const { data: step1Data, loading: step1Loading } = useFetchQuery({
    query: getPlannerElementNodeSetIdsOfCurriculumQuery,
    skipCondition: _.isEmpty(curriculumId) || _.isEmpty(plannerElementTypes),
    variables: getPlannerElementNodeSetIdsOfCurriculumVariables({
      curriculumId,
      curriculumNodeInterfaceType,
      plannerElementTypes,
    }),
    fetchPolicy: "cache-and-network",
    parseData: data =>
      _.map(_.get(data, "node.plannerElementNodeSets.edges", []), "node"),
  });

  const plannerElementNodeSets = step1Data || [];

  // Step 2a: grade-dependent nodes
  const { data: gradeData, loading: gradeLoading } = useFetchQuery({
    query: getPlannerElementNodeSetsFromIdsQuery,
    skipCondition:
      step1Loading ||
      _.isEmpty(plannerElementNodeSets) ||
      _.isEmpty(
        learningGoalsQueryVariables?.gradeDependentLearningGoalVariables
          ?.elementTypes
      ),
    variables: getPlannerElementNodeSetsFromIdsVariables({
      plannerElementNodeSets,
      academicYearId,
      queryVariables:
        learningGoalsQueryVariables?.gradeDependentLearningGoalVariables,
    }),
    fetchPolicy: "cache-and-network",
    parseData: data =>
      getPlannerElementNodeSetData({
        plannerElementNodeSets: _.get(data, "multiNode", []),
      }),
  });

  // Step 2b: subject-dependent nodes
  const { data: subjectData, loading: subjectLoading } = useFetchQuery({
    query: getPlannerElementNodeSetsFromIdsQuery,
    skipCondition:
      step1Loading ||
      _.isEmpty(plannerElementNodeSets) ||
      _.isEmpty(
        learningGoalsQueryVariables?.subjectDependentLearningGoalVariables
          ?.elementTypes
      ),
    variables: getPlannerElementNodeSetsFromIdsVariables({
      plannerElementNodeSets,
      academicYearId,
      queryVariables:
        learningGoalsQueryVariables?.subjectDependentLearningGoalVariables,
    }),
    fetchPolicy: "cache-and-network",
    parseData: data =>
      getPlannerElementNodeSetData({
        plannerElementNodeSets: _.get(data, "multiNode", []),
      }),
  });

  // Step 2c: subType-dependent nodes
  const { data: subTypeData, loading: subTypeLoading } = useFetchQuery({
    query: getPlannerElementNodeSetsFromIdsQuery,
    skipCondition:
      step1Loading ||
      _.isEmpty(plannerElementNodeSets) ||
      _.isEmpty(
        learningGoalsQueryVariables?.subTypeDependentLearningGoalVariables
          ?.elementTypes
      ),
    variables: getPlannerElementNodeSetsFromIdsVariables({
      plannerElementNodeSets,
      academicYearId,
      queryVariables:
        learningGoalsQueryVariables?.subTypeDependentLearningGoalVariables,
    }),
    fetchPolicy: "cache-and-network",
    parseData: data =>
      getPlannerElementNodeSetData({
        plannerElementNodeSets: _.get(data, "multiNode", []),
        isSubTypeDependent: true,
      }),
  });

  // Step 2d: curriculum-dependent nodes (no additional filters)
  const { data: curriculumData, loading: curriculumLoading } = useFetchQuery({
    query: getPlannerElementNodeSetsFromIdsQuery,
    skipCondition:
      step1Loading ||
      _.isEmpty(plannerElementNodeSets) ||
      _.isEmpty(
        learningGoalsQueryVariables?.curriculumDependentLearningGoalVariables
          ?.elementTypes
      ),
    variables: getPlannerElementNodeSetsFromIdsVariables({
      plannerElementNodeSets,
      academicYearId,
      queryVariables:
        learningGoalsQueryVariables?.curriculumDependentLearningGoalVariables,
    }),
    fetchPolicy: "cache-and-network",
    parseData: data =>
      getPlannerElementNodeSetData({
        plannerElementNodeSets: _.get(data, "multiNode", []),
      }),
  });

  const learningGoalsData = [
    ...(gradeData || []),
    ...(subjectData || []),
    ...(subTypeData || []),
    ...(curriculumData || []),
  ];

  const isLoading =
    step1Loading ||
    gradeLoading ||
    subjectLoading ||
    subTypeLoading ||
    curriculumLoading;

  return { learningGoalsData, isLoading };
};
